/**
 * Multiplayer network protocol: MessagePack wire encoding plus shared message
 * types used by `NetworkGameSession` (client) and `ServerGameSession` (server).
 */
import { decode, encode } from '@msgpack/msgpack';

import type {
  ClientId,
  GameSessionDescription,
  JoinParams,
  ReplicationAck,
  ReplicationPacket
} from '../interface.js';

// ---------------------------------------------------------------------------
// MessagePack wire codec
// ---------------------------------------------------------------------------

/** Encode a logical message to a MessagePack payload for the WebSocket. */
export function encodeWireMessage(value: unknown): Uint8Array {
  return encode(value) as Uint8Array;
}

/** Decode a MessagePack payload from the WebSocket to a JavaScript value. */
export function decodeWireMessage(data: Uint8Array): unknown {
  return decode(data);
}

// ---------------------------------------------------------------------------
// Message types and envelopes
// ---------------------------------------------------------------------------

/**
 * Wire-level message types.
 */
export enum MessageType {
  /** Initial handshake exchanged once per connection. */
  HANDSHAKE = 'handshake',

  /** Periodic liveness ping from the client. */
  CLIENT_HEARTBEAT = 'client_heartbeat',
  /** Client requests the list of clients in the session. */
  GET_CLIENTS = 'get_clients',

  /** Remote procedure call (client→server or server→client). */
  RPC = 'rpc',

  /** Server → client: actor replication payload in {@link ReplicationMessage.data}. */
  REPLICATION = 'replication',

  /** Client → server: replication acknowledgments in {@link ReplicationAckMessage.data}. */
  REPLICATION_ACK = 'replication_ack',

  /** Round-trip-time measurement (fire-and-forget). */
  PING = 'ping',
  PONG = 'pong',

  /** Client requests a graceful disconnect. */
  DISCONNECT = 'disconnect'
}

/**
 * Wire envelope for server → client replication.
 */
export interface ReplicationMessage {
  type: MessageType.REPLICATION;
  data: ReplicationPacket;
}

/**
 * Wire envelope for client → server replication acknowledgments.
 */
export interface ReplicationAckMessage {
  type: MessageType.REPLICATION_ACK;
  data: ReplicationAck;
}

/** Base interface for any request/response message. */
export interface BaseMessage {
  type: MessageType;
  /** Request ID for matching responses. */
  id: string;
}

/** Client-to-server request that expects a response. */
export interface RequestMessage extends BaseMessage {
  data?: any;
}

/** Server-to-client response to a {@link RequestMessage}. */
export interface ResponseMessage extends BaseMessage {
  success: boolean;
  data?: any;
  error?: string;
}

/**
 * Handshake request payload (client → server).
 *
 * The client sends this immediately after the WebSocket opens. Any data the
 * client used to send on a separate join RPC must be supplied here under
 * `joinParams` — the server uses it for `GameMode.canPlayerJoin()` and to
 * fire `handlePlayerJoined` as part of the handshake.
 */
export interface HandshakeRequestData {
  /** Params forwarded to `GameMode.canPlayerJoin()`. */
  joinParams?: JoinParams;
  /** Optional client-reported version strings for diagnostics. */
  clientEngineVersion?: string;
  clientGameVersion?: string;
}

/**
 * Handshake response payload (server → client).
 */
export interface HandshakeResponseData {
  /** Server-assigned client ID. */
  clientId: ClientId;
  /** Server's reported engine version. */
  engineVersion: string;
  /** Server's reported game version. */
  gameVersion: string;
  /** Description of the single game session hosted by this server. */
  gameSession: GameSessionDescription;
}

/**
 * RPC (Remote Procedure Call) message.
 *
 * The outer envelope is intentionally minimal: all RPC-specific fields
 * (objectId, methodName, netSubId, args) are encoded in the binary `payload`
 * produced by {@link encodeRpcPayload} and decoded by {@link decodeRpcHeader}.
 *
 * `senderId` is the only envelope field added by the server before forwarding
 * to clients; the sender never includes it.
 */
export interface RpcMessage {
  type: MessageType.RPC;
  /** Full binary RPC payload (header + args). See {@link encodeRpcPayload}. */
  payload: Uint8Array;
  /** Set by the server when forwarding a client-originated RPC. */
  senderId?: ClientId;
}

/**
 * Ping/Pong envelope for round-trip-time measurement.
 *
 * Either side can send a `PING` and the other immediately echoes a `PONG`
 * with the same `pingId` and original `sentAt`. The originator computes
 * `Date.now() - sentAt` on receipt and stores it on the connection's stats.
 *
 * Sent as fire-and-forget messages (no `RequestMessage` correlation) to
 * keep the per-second ping path lightweight.
 */
export interface PingMessage {
  type: MessageType.PING | MessageType.PONG;
  pingId: string;
  /** Wall-clock time (ms since epoch) the originator sent the PING. */
  sentAt: number;
}
